import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { fetchIcalFeeds, syncIcalFeeds, updateIcalFeed, createIcalFeed } from "~/apis/settings"
import { fetchProperties } from "~/apis/properties"
import { pushSupported, currentPushSubscription, enablePush, disablePush } from "~/lib/push"
import TemplateEditors from "~/components/TemplateEditors"
import PropertyEditors from "~/components/PropertyEditors"

const SOURCES = [ "airbnb", "riparide" ]

function timeAgo(iso) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000))
  const units = [ [ "year", 31536000 ], [ "month", 2592000 ], [ "day", 86400 ], [ "hour", 3600 ], [ "minute", 60 ] ]

  for (const [ unit, secondsPerUnit ] of units) {
    const value = Math.floor(seconds / secondsPerUnit)
    if (value >= 1) return `${value} ${unit}${value === 1 ? "" : "s"} ago`
  }

  return "less than a minute ago"
}

function FeedRow({ feed, onSaved }) {
  const [ editing, setEditing ] = useState(false)
  const [ url, setUrl ] = useState(feed.url)
  const [ error, setError ] = useState(null)
  const [ saving, setSaving ] = useState(false)

  function startEditing() {
    setUrl(feed.url)
    setError(null)
    setEditing(true)
  }

  async function handleSave(event) {
    event.preventDefault()
    setSaving(true)
    setError(null)

    try {
      const updated = await updateIcalFeed(feed.id, url)
      onSaved(updated)
      setEditing(false)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className="py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="font-medium text-foreground">
            {feed.property_name} — {feed.source}
          </p>
          <p className="text-sm text-muted-foreground">
            {feed.last_sync_error
              ? `Last sync failed: ${feed.last_sync_error}`
              : feed.last_synced_at
                ? `Last synced ${timeAgo(feed.last_synced_at)}`
                : "Never synced"}
          </p>
          {!editing && <p className="mt-1 truncate text-sm text-muted-foreground">{feed.url}</p>}
        </div>
        {!editing && (
          <Button variant="outline" size="sm" className="shrink-0" onClick={startEditing}>
            Edit URL
          </Button>
        )}
      </div>

      {editing && (
        <form onSubmit={handleSave} className="mt-2 flex items-center gap-2">
          <Input
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://…"
            autoFocus
            className="flex-1"
          />
          <Button type="submit" size="sm" disabled={saving}>Save</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        </form>
      )}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </li>
  )
}

function NewFeedForm({ properties, onCreated }) {
  const [ propertyId, setPropertyId ] = useState(properties[0]?.id ? String(properties[0].id) : "")
  const [ source, setSource ] = useState(SOURCES[0])
  const [ url, setUrl ] = useState("")
  const [ error, setError ] = useState(null)
  const [ busy, setBusy ] = useState(false)

  async function submit(event) {
    event.preventDefault()
    if (!propertyId || !url.trim()) return

    setBusy(true)
    setError(null)
    try {
      const feed = await createIcalFeed({ propertyId, source, url })
      onCreated(feed)
      setUrl("")
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (properties.length === 0) {
    return <p className="mt-4 text-sm text-muted-foreground">Add a property above before adding a calendar feed.</p>
  }

  return (
    <form onSubmit={submit} className="mt-4 space-y-2 rounded-lg border border-border p-3">
      <p className="text-sm font-medium text-foreground">Add a calendar feed</p>
      <div className="grid grid-cols-2 gap-2">
        <Select value={propertyId} onValueChange={setPropertyId}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {properties.map((property) => (
              <SelectItem key={property.id} value={String(property.id)}>{property.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {SOURCES.map((value) => (
              <SelectItem key={value} value={value}>{value}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://…" />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" size="sm" disabled={busy}>Add feed</Button>
    </form>
  )
}

const pushConfigured = !!document.querySelector('meta[name="vapid-public-key"]')?.content

export default function Settings() {
  const [ feeds, setFeeds ] = useState([])
  const [ properties, setProperties ] = useState([])
  const [ message, setMessage ] = useState(null)
  const [ pushEnabled, setPushEnabled ] = useState(false)
  const [ pushError, setPushError ] = useState(null)

  useEffect(() => {
    load()
    fetchProperties().then(setProperties)
    if (pushSupported()) currentPushSubscription().then((subscription) => setPushEnabled(!!subscription))
  }, [])

  function load() {
    fetchIcalFeeds().then(setFeeds)
  }

  function handleFeedSaved(updated) {
    setFeeds((current) => current.map((feed) => (feed.id === updated.id ? updated : feed)))
  }

  function handleFeedCreated(feed) {
    setFeeds((current) => [ ...current, feed ])
  }

  async function handleSync() {
    const { message } = await syncIcalFeeds()
    setMessage(message)
    load()
  }

  async function handleTogglePush() {
    setPushError(null)

    try {
      if (pushEnabled) {
        await disablePush()
        setPushEnabled(false)
      } else {
        await enablePush()
        setPushEnabled(true)
      }
    } catch (err) {
      setPushError(err.message)
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-12">
      <h1 className="text-2xl font-semibold text-foreground">Settings</h1>

      {message && (
        <p className="mt-4 rounded-md bg-emerald-50 px-4 py-2 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
          {message}
        </p>
      )}

      <section className="mt-8">
        <h2 className="text-lg font-medium text-foreground">Properties</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Locality, colour, check-in/out times, and notes for cleaners.
        </p>
        <PropertyEditors />
      </section>

      <section className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-foreground">Calendar feeds</h2>
          {feeds.length > 0 && <Button onClick={handleSync}>Sync calendars now</Button>}
        </div>

        {feeds.length === 0 && (
          <p className="mt-4 text-sm text-muted-foreground">No calendar feeds yet.</p>
        )}

        <ul className="mt-4 divide-y divide-border">
          {feeds.map((feed) => (
            <FeedRow key={feed.id} feed={feed} onSaved={handleFeedSaved} />
          ))}
        </ul>

        <NewFeedForm properties={properties} onCreated={handleFeedCreated} />
      </section>

      <section className="mt-8">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-md">
            <h2 className="text-lg font-medium text-foreground">Notifications on your phone</h2>
            <p className="text-sm text-muted-foreground">
              A push when a booking arrives or changes, a cleaner says yes or no, a clean is done, a note comes in, or a guest message is due.
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              On iPhone: open this site in Safari, Share → Add to Home Screen, then open the app from the icon and enable notifications here.
            </p>
          </div>
          {pushSupported() && (
            <Button variant={pushEnabled ? "outline" : "default"} disabled={!pushConfigured} onClick={handleTogglePush}>
              {pushEnabled ? "Disable on this device" : "🔔 Enable on this device"}
            </Button>
          )}
        </div>
        {!pushConfigured && (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">Push keys aren't configured on the server yet (VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY).</p>
        )}
        {!pushSupported() && (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">This browser can't receive push notifications — on iPhone, open the app from your Home Screen icon.</p>
        )}
        {pushError && <p className="mt-2 text-sm text-destructive">{pushError}</p>}
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-medium text-foreground">Message templates</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Editable guest and cleaner messages, per property.
        </p>
        <TemplateEditors />
      </section>
    </div>
  )
}
